import { Badge } from "@hortis/ui/badge";
import { Button } from "@hortis/ui/button";
import { DatePicker, createDateLabel } from "@hortis/ui/date-picker";
import { DialogTrigger } from "@hortis/ui/dialog";
import { FormControl, FormField, FormItem, FormMessage } from "@hortis/ui/form";
import {
  Building6,
  ChevronDown,
  Globe5,
  Package,
  Tag3,
} from "@hortis/ui/icons";
import { CalendarPlus2 } from "@hortis/ui/icons/calendar-plus-2";
import { Globe2 } from "@hortis/ui/icons/globe-2";
import { RichTextEditor } from "@hortis/ui/rich-text-editor";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@hortis/ui/tooltip";
import { useRouteContext } from "@tanstack/react-router";
import { ProvenanceCategoryCode } from "generated/graphql";
import type { MutableRefObject } from "react";
import { useFormContext } from "react-hook-form";
import {
  TaxonPicker,
  TaxonPickerLabel,
} from "src/components/autocomplete/scientific-names/taxon-picker";
import { MaterialReceivedPicker } from "src/components/pickers/material-received-picker";
import { ProvenancePicker } from "src/components/pickers/provenance-picker";
import { TagMultiPicker } from "src/components/pickers/tag-picker/tag-picker";
import { mention } from "src/components/rich-text-editor/mention";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { provenanceLabelMap } from "src/utils/label-maps/provenance";
import { useAccessTokenBasic } from "src/utils/use-access-token";
import { twMerge } from "tailwind-merge";
import type { CreateAccessionFormSchemaInput } from "../schema";
import { AccessionFieldsMoreDropdown } from "./accession-fields-more-dropdown";
import { DonorPicker } from "./donor-picker";
import { MaterialSection } from "./material-section";
import {
  WildCollectionDialog,
  useWildCollectionFieldCount,
} from "./wild-collection-dialog";

export const NewAccessionDataFields = ({
  activeMaterial,
  setActiveMaterial,
  taxonPickerRef,
  siteSlug,
}: {
  activeMaterial: null | number;
  setActiveMaterial: (val: null | number) => void;
  taxonPickerRef?: MutableRefObject<HTMLButtonElement | null>;
  siteSlug: string;
}) => {
  const { subdomain } = useRouteContext({ from: "__root__" });
  const form = useFormContext<Partial<CreateAccessionFormSchemaInput>>();
  const wildCollection = form.watch("wildCollection");
  const accessToken = useAccessTokenBasic();
  const wildCollectionFieldCount = useWildCollectionFieldCount();
  const provenance = form.watch("provenanceCategoryCode");

  return (
    <div className="flex flex-col gap-4">
      <FormField
        control={form.control}
        name="taxonOption"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormControl>
              <TaxonPicker data-cy="taxon-picker-new" {...field}>
                <Button
                  className={twMerge(
                    "w-full justify-between px-[14px]!",
                    // eslint-disable-next-line sonarjs/no-duplicate-string
                    field.value == null && "text-grey-500",
                    fieldState.error &&
                      "border-error-500 text-error-700 hover:border-error-500 hover:bg-error-25 hover:text-error-700",
                  )}
                  endIcon={ChevronDown}
                  ref={taxonPickerRef}
                >
                  {field.value == null ? (
                    "Select taxon..."
                  ) : (
                    <TaxonPickerLabel value={field.value} />
                  )}
                </Button>
              </TaxonPicker>
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="notes"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              {accessToken != null && (
                <RichTextEditor
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Add notes..."
                  data-cy="accession-notes-input"
                  extensions={[mention({ accessToken, siteSlug, subdomain })]}
                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <TooltipProvider delayDuration={1000}>
        <div className="flex flex-wrap gap-2 pb-2">
          <FormField
            control={form.control}
            name="accessionDate"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Tooltip>
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      data-cy="accession-date-picker"
                    >
                      <TooltipTrigger asChild>
                        <Button startIcon={CalendarPlus2} size="xs">
                          {field.value == null
                            ? "Select an accession date..."
                            : createDateLabel(field.value)}
                        </Button>
                      </TooltipTrigger>
                    </DatePicker>
                    <TooltipContent side="bottom" sideOffset={0}>
                      Change accession date
                    </TooltipContent>
                  </Tooltip>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="materialTypeId"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Tooltip>
                    <MaterialReceivedPicker
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={
                        field.value == null
                          ? "Set material received..."
                          : "Change material received..."
                      }
                    >
                      <TooltipTrigger asChild>
                        <Button
                          className={twMerge(
                            field.value == null && "text-grey-500",
                          )}
                          size="xs"
                          startIcon={Package}
                        >
                          {field.value == null
                            ? "Material received"
                            : field.value.name}
                        </Button>
                      </TooltipTrigger>
                    </MaterialReceivedPicker>
                    <TooltipContent side="bottom" sideOffset={0}>
                      {field.value == null
                        ? "Set material received"
                        : "Change material received"}
                    </TooltipContent>
                  </Tooltip>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex">
            <FormField
              control={form.control}
              name="provenanceCategoryCode"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <Tooltip>
                      <ProvenancePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Set provenance..."
                      >
                        <TooltipTrigger asChild>
                          <Button
                            className={twMerge(
                              "max-w-[138px] truncate",
                              field.value == null && "text-grey-500",
                              fieldState.error &&
                                "border-error-500 text-error-700 hover:border-error-500 hover:bg-error-25 hover:text-error-700",
                              (field.value === ProvenanceCategoryCode.W ||
                                field.value === ProvenanceCategoryCode.Z) &&
                                "rounded-r-none",
                            )}
                            size="xs"
                            startIcon={Globe2}
                          >
                            {field.value == null ? (
                              <div>
                                Provenance
                                <span className="ml-0.5 text-orange-500">
                                  *
                                </span>
                              </div>
                            ) : (
                              <span className="truncate">
                                {provenanceLabelMap[field.value]}
                              </span>
                            )}
                          </Button>
                        </TooltipTrigger>
                      </ProvenancePicker>
                      <TooltipContent side="bottom" sideOffset={0}>
                        Set provenance
                      </TooltipContent>
                    </Tooltip>
                  </FormControl>
                </FormItem>
              )}
            />
            {(provenance === ProvenanceCategoryCode.W ||
              provenance === ProvenanceCategoryCode.Z) && (
              <WildCollectionDialog
                onSubmit={(data) => {
                  form.setValue("wildCollection", data, { shouldDirty: true });
                }}
                initialData={wildCollection ?? undefined}
              >
                <Tooltip>
                  <DialogTrigger asChild onFocusCapture={stopEventPropagation}>
                    <TooltipTrigger asChild>
                      <Button
                        size="xs"
                        className={twMerge(
                          "rounded-l-none border-l-0!",
                          wildCollectionFieldCount === 0
                            ? "text-grey-500"
                            : "py-1 pr-2.5",
                        )}
                        startIcon={Globe5}
                        testId="wild-collection-button"
                      >
                        Wild collection
                        {wildCollectionFieldCount > 0 && (
                          <Badge
                            color="grey"
                            type="icon"
                            className="rounded-md px-1.5"
                          >
                            {wildCollectionFieldCount}
                          </Badge>
                        )}
                      </Button>
                    </TooltipTrigger>
                  </DialogTrigger>
                  <TooltipContent side="bottom" sideOffset={0}>
                    {wildCollectionFieldCount > 0 ? "Change" : "Add"} wild
                    collection details
                  </TooltipContent>
                </Tooltip>
              </WildCollectionDialog>
            )}
          </div>
          <FormField
            control={form.control}
            name="donor"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Tooltip>
                    <DonorPicker
                      newDonorOption
                      placeholder="Set supplier..."
                      {...field}
                    >
                      <TooltipTrigger asChild>
                        <Button
                          startIcon={Building6}
                          size="xs"
                          className={twMerge(
                            "max-w-48",
                            (field.value == null || field.value === "") &&
                              "text-grey-500",
                          )}
                        >
                          {field.value == null || field.value === "" ? (
                            "Supplier"
                          ) : (
                            <div className="truncate text-grey-700">
                              {field.value}
                            </div>
                          )}
                        </Button>
                      </TooltipTrigger>
                    </DonorPicker>
                    <TooltipContent side="bottom" sideOffset={0}>
                      Set supplier
                    </TooltipContent>
                  </Tooltip>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Tooltip>
                    <TagMultiPicker
                      data-cy="accession-tags-picker"
                      placeholder={
                        field.value == null || field.value.length === 0
                          ? "Add tags..."
                          : "Change tags..."
                      }
                      tagLevel="site"
                      newTagOption
                      {...field}
                    >
                      <TooltipTrigger asChild>
                        <Button
                          startIcon={Tag3}
                          size="xs"
                          className={twMerge(
                            "max-w-48",
                            (field.value == null || field.value.length === 0) &&
                              "text-grey-500",
                          )}
                        >
                          {field.value == null || field.value.length === 0 ? (
                            "Tags"
                          ) : (
                            <div className="truncate">
                              {field.value.length === 1
                                ? field.value[0]?.name
                                : `${field.value.length} tags`}
                            </div>
                          )}
                        </Button>
                      </TooltipTrigger>
                    </TagMultiPicker>
                    <TooltipContent side="bottom" sideOffset={0}>
                      {field.value == null || field.value.length === 0
                        ? "Add tags"
                        : "Change tags"}
                    </TooltipContent>
                  </Tooltip>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <AccessionFieldsMoreDropdown />
        </div>
        <MaterialSection
          activeMaterial={activeMaterial}
          setActiveMaterial={setActiveMaterial}
          siteSlug={siteSlug}
        />
      </TooltipProvider>
    </div>
  );
};
